import {
  Avatar,
  Box,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemText,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';

import { Chat, Clear, Search } from '@mui/icons-material';

import FilePresentIcon from '@mui/icons-material/FilePresent';

import { useRef, useState } from 'react';

import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useDebounce } from '@uidotdev/usehooks';

import { Or2Card } from '@/components/Surface/Or2Card';
import { Or2ListItemButton } from '@/components/data-display/Or2ListItemButton';
import { graphql } from '@/gql';
import {
  extractPaginationProps,
  extractConnectionProps,
} from '@/utils/queries/queryExtracts';

import { Or2ListItem } from '@/components/data-display/Or2ListItem';

import { InfiniteScroll } from '@/components/infinite-scroll/InfiniteScroll';

import { AssistantDeleteConversationButton } from './recent-conversations/AssistantDeleteConversationButton';

const PAGE_SIZE = 20;

export const ASSISTANT_RECENT_CONVERSATIONS_QUERY = graphql(`
  query AssistantRecentConversations(
    $first: Int
    $after: String
    $search: String
  ) {
    currentUser {
      _id
      ortooGptConversationConnection(
        first: $first
        after: $after
        search: $search
      ) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            _id
            caption
            itemsConnection {
              edges {
                node {
                  _id
                  prompt {
                    fileIdsConnection {
                      edges {
                        node {
                          _id
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);

interface AssistantRecentConversationsProps {
  open: boolean;
  onConversationSelect: (id: string | null) => void;
}

export const AssistantRecentConversations = ({
  open,
  onConversationSelect,
}: AssistantRecentConversationsProps) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const searchVariable = debouncedSearch.length >= 3 ? debouncedSearch : null;

  const { data, fetchMore, networkStatus } = useQuery(
    ASSISTANT_RECENT_CONVERSATIONS_QUERY,
    {
      variables: {
        first: PAGE_SIZE,
        after: null,
        search: searchVariable,
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !open,
    },
  );

  const loading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const {
    nodes: conversations,
    hasNextPage,
    endCursor,
  } = extractPaginationProps(data?.currentUser?.ortooGptConversationConnection);

  const handleLoadMoreRef = useRef<() => void>(undefined);
  handleLoadMoreRef.current = () => {
    if (!hasNextPage || !endCursor || isFetchingMore) return;

    fetchMore({
      variables: {
        after: endCursor,
      },
    });
  };

  const handleLoadMore = useRef(() => {
    handleLoadMoreRef.current?.();
  }).current;

  const hasAnyFilesInConversations = conversations.some((conversation) =>
    extractConnectionProps(conversation?.itemsConnection).some(
      (item) =>
        extractConnectionProps(item?.prompt?.fileIdsConnection).length > 0,
    ),
  );

  return (
    <Or2Card or2Variant="inner">
      <CardHeader
        title="Recent conversations"
        avatar={
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <Chat />
          </Avatar>
        }
      />
      <Divider />
      <Box sx={{ px: 2, pt: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          helperText={
            search.length > 0 && search.length < 3
              ? 'Enter 3 or more characters to search'
              : undefined
          }
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear search"
                    size="small"
                    onClick={() => setSearch('')}
                    edge="end"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </Box>
      {!loading && conversations.length === 0 ? (
        <CardContent>
          <Typography variant="body2">
            {searchVariable
              ? 'No conversations match your search'
              : "You don't have any conversations yet"}
          </Typography>
        </CardContent>
      ) : (
        <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
          <InfiniteScroll
            hasMore={hasNextPage}
            isLoading={isFetchingMore}
            initialLoader={null}
            load={handleLoadMore}
            loader={
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            }
          >
            <List dense>
              {conversations.map((conversation) => {
                const { _id: conversationId, caption } = conversation;

                const hasFiles = extractConnectionProps(
                  conversation?.itemsConnection,
                ).some(
                  (item) =>
                    extractConnectionProps(item?.prompt?.fileIdsConnection)
                      .length > 0,
                );
                return (
                  <Or2ListItem
                    key={conversationId}
                    secondaryAction={
                      <AssistantDeleteConversationButton
                        conversation={conversation}
                        size="small"
                      />
                    }
                    disablePadding
                    sx={{
                      '& .MuiListItemSecondaryAction-root': {
                        sm: {
                          opacity: 0,
                          transition: 'opacity 0.2s',
                        },
                      },
                      '&:hover .MuiListItemSecondaryAction-root': {
                        sm: {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <Or2ListItemButton
                      onClick={() => onConversationSelect(conversationId)}
                    >
                      <>
                        {hasAnyFilesInConversations &&
                          (hasFiles ? (
                            <FilePresentIcon
                              fontSize="small"
                              color="action"
                              sx={{ mr: 2 }}
                            />
                          ) : (
                            <Box sx={{ width: 20, mr: 2 }} />
                          ))}
                        {caption ? (
                          <ListItemText primary={caption} />
                        ) : (
                          <Skeleton width="60%" />
                        )}
                      </>
                    </Or2ListItemButton>
                  </Or2ListItem>
                );
              })}
            </List>
          </InfiniteScroll>
        </Box>
      )}
    </Or2Card>
  );
};
